/* eslint-disable react/destructuring-assignment */
import React from 'react';

import * as styles from './styles';
import { ListItem } from './components';
import classnames from 'classnames';
import moment from 'moment';

import {
  INCIDENTS_REQUEST_AMOUNT,
  type IncidentWithAddress,
  type TimestampInMillis,
} from '@cobliteam/common-types';

import { isWebDataSuccess, type WebData } from '@cobliteam/api-lib';

import {
  BackToTopButton,
  CaptionDefault,
  Divider,
  Heading1,
  Loading,
  Placeholder,
  PlaceholderNotificationsIcon,
} from '@cobliteam/base-components';

import { Button } from '@cobliteam/ui/components';

import SaveAltRoundedIcon from '@mui/icons-material/SaveAltRounded';

export interface IncidentsPopupProps {
  incidentsWebData: WebData<IncidentWithAddress[]>;
  loadIncidents: (endDate: TimestampInMillis) => void;
  handleExportClick: () => void;
}

export interface IncidentsPopupState {
  incidents: IncidentWithAddress[];
  hasMoreIncidents: boolean;
  isScrollAtTop: boolean;
}

export class IncidentsPopup extends React.Component<
  IncidentsPopupProps,
  IncidentsPopupState
> {
  private popupRef = React.createRef<HTMLDivElement>();

  constructor(props: IncidentsPopupProps) {
    super(props);

    this.state = {
      incidents: [],
      hasMoreIncidents: true,
      isScrollAtTop: true,
    };
  }

  public componentDidMount = () => {
    const endDate = moment().endOf('day').valueOf();

    this.props.loadIncidents(endDate);
  };

  public componentDidUpdate = (prevProps: IncidentsPopupProps) => {
    const { incidentsWebData } = this.props;
    const { incidents } = this.state;

    if (
      !isWebDataSuccess(prevProps.incidentsWebData) &&
      isWebDataSuccess(incidentsWebData)
    ) {
      this.setState({
        incidents: incidents.concat(incidentsWebData.data),
      });
    }
  };

  public static getDerivedStateFromProps = (props: IncidentsPopupProps) => {
    const { incidentsWebData } = props;
    let hasMoreIncidents = true;

    if (isWebDataSuccess(incidentsWebData)) {
      hasMoreIncidents =
        incidentsWebData.data.length === INCIDENTS_REQUEST_AMOUNT;
    }

    return { hasMoreIncidents };
  };

  private getPopupRef = () => {
    return this.popupRef;
  };

  private getPopupCurrent = () => {
    const ref = this.getPopupRef();

    if ('current' in ref) {
      return ref.current;
    }

    return null;
  };

  private onScrollPopup = () => {
    const popupCurrent = this.getPopupCurrent();
    const { incidentsWebData } = this.props;
    const { hasMoreIncidents } = this.state;

    if (popupCurrent) {
      const isScrollAtTop = popupCurrent.scrollTop === 0;

      if (!isScrollAtTop) {
        const isScrollAtBottom =
          popupCurrent.scrollTop ===
          popupCurrent.scrollHeight - popupCurrent.offsetHeight;

        if (isScrollAtBottom && isWebDataSuccess(incidentsWebData)) {
          const incidents = incidentsWebData.data;
          const endDate =
            incidents[incidents.length - 1] &&
            incidents[incidents.length - 1].date - 1;

          if (hasMoreIncidents) {
            this.props.loadIncidents(endDate);
          }
        }
      }

      this.setState({ isScrollAtTop });
    }
  };

  private onClickBackToTopButton = () => {
    const sectionCurrent = this.getPopupCurrent();

    if (sectionCurrent) {
      sectionCurrent.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  private renderList = () => {
    const { incidentsWebData } = this.props;
    const { incidents, hasMoreIncidents } = this.state;
    const listEnd = hasMoreIncidents ? (
      <Loading />
    ) : (
      <CaptionDefault>Não existem mais notificações</CaptionDefault>
    );

    if (incidents.length === 0) {
      return (
        <div className={styles.placeholderOrLoading}>
          {isWebDataSuccess(incidentsWebData) ? (
            <Placeholder
              className={styles.placeholder}
              text={
                'Você não possui alertas dos veículos. \n Aproveite seu dia!'
              }
              Icon={PlaceholderNotificationsIcon}
            />
          ) : (
            <Loading />
          )}
        </div>
      );
    }

    return (
      <>
        <ul className={styles.incidentsList}>
          {incidents.map((incident, index) => (
            <li key={incident.id}>
              <ListItem incident={incident} />
              {index < incidents.length - 1 && (
                <Divider className={styles.divider} />
              )}
            </li>
          ))}
        </ul>
        <div className={styles.listEnd}>{listEnd}</div>
      </>
    );
  };

  public render = () => {
    const { handleExportClick } = this.props;
    const { incidents, isScrollAtTop } = this.state;
    const hasIncidents = incidents.length > 0;

    return (
      <div className={styles.incidentsPopupWrapper}>
        <div
          ref={this.getPopupRef()}
          className={classnames(styles.incidentsPopup, {
            hasIncidents,
          })}
          onScroll={this.onScrollPopup}
        >
          <div className={styles.incidentsPopupHeader}>
            <Heading1>Alertas</Heading1>
            <Button
              size='condensed'
              disabled={!hasIncidents}
              startIcon={<SaveAltRoundedIcon />}
              className='min-w-32'
              onClick={handleExportClick}
            >
              Exportar
            </Button>
          </div>
          {this.renderList()}

          {!isScrollAtTop && (
            <BackToTopButton
              onClick={this.onClickBackToTopButton}
              className={styles.backToTopButton}
            />
          )}
        </div>
      </div>
    );
  };
}
